import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAnioLectivo } from '../contexts/AnioLectivoContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { dashboardService } from '../services/dashboardService';
import './Inicio.css';

const Inicio = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { user, hasRole, hasPermission } = useAuth();
    const { selectedAnio } = useAnioLectivo();
    const [viewMode, setViewMode] = useState('modules'); // 'modules' | 'processes'
    const [kanbanData, setKanbanData] = useState([]);
    const [loadingKanban, setLoadingKanban] = useState(false);
    const [errorKanban, setErrorKanban] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (viewMode === 'processes' && selectedAnio) {
            fetchKanbanData();
        }
    }, [viewMode, selectedAnio]);

    const fetchKanbanData = async () => {
        if (!selectedAnio) return;

        setLoadingKanban(true);
        setErrorKanban(null);
        try {
            const data = await dashboardService.getMatriculaKanban(selectedAnio.id);
            setKanbanData(data);
        } catch (err) {
            console.error('Error fetching kanban:', err);
            setErrorKanban('No se pudieron cargar los datos del tablero.');
        } finally {
            setLoadingKanban(false);
        }
    };

    // --- CONFIG: MODULES (Updated with sub-pages) ---
    const modulesConfig = [
        {
            title: 'General',
            icon: 'flaticon-025-dashboard',
            color: 'primary',
            subpages: [
                { title: 'Dashboard', to: '/', icon: 'fa fa-chart-line' },
            ]
        },
        {
            title: 'Gestión Académica',
            icon: 'flaticon-043-menu',
            color: 'primary',
            permission: 'GESTION_ACADEMICA_VER',
            subpages: [
                { title: 'Usuarios Gral.', to: '/usuarios/lista', icon: 'fa fa-address-book' },
                { title: 'Usuarios Estudiantes', to: '/usuarios/estudiantes', icon: 'fa fa-user-graduate' },
                { title: 'Usuarios Personal', to: '/usuarios/personal', icon: 'fa fa-users-cog' },
                { title: 'Usuarios Apoderados', to: '/usuarios/apoderados', icon: 'fa fa-user-tie' },
                { title: 'Matrícula', to: '/matriculas', icon: 'fa fa-id-card' },
                { title: 'Años Lectivos', to: '/anios-lectivos', icon: 'fa fa-calendar-check' },
                { title: 'Sedes', to: '/sedes', icon: 'fa fa-building' },
                { title: 'Niveles', to: '/niveles', icon: 'fa fa-layer-group' },
                { title: 'Grados', to: '/grados', icon: 'fa fa-signal' },
                { title: 'Aulas', to: '/aulas', icon: 'fa fa-door-open' },
                { title: 'Vista Estructura (Beta)', to: '/academico/estructura', icon: 'fa fa-sitemap' },
            ]
        },
        {
            title: 'Módulo Académico',
            icon: 'flaticon-381-diploma',
            color: 'success',
            permission: 'MODULO_ACADEMICO_VER',
            subpages: [
                { title: 'Áreas Académicas', to: '/academico/areas', icon: 'fa fa-book' },
                { title: 'Asignaturas', to: '/academico/asignaturas', icon: 'fa fa-journal-whills' },
                { title: 'Malla Curricular', to: '/academico/malla-curricular', icon: 'fa fa-network-wired' },
                { title: 'Gestión de Cursos', to: '/academico/cursos', icon: 'fa fa-laptop-code' },
                { title: 'Gestión de Alumnos', to: '/academico/alumnos', icon: 'fa fa-user-check' },
                { title: 'Sábana Notas', to: '/academico/sabana-notas', icon: 'fa fa-th-list' },
                { title: 'Libreta de Notas', to: '/academico/libreta-notas', icon: 'fa fa-file-alt' },
                { title: 'Generador Barcodes', to: '/academico/barcodes', icon: 'fa fa-barcode' },
            ]
        },
        {
            title: 'Caja y Pagos',
            icon: 'flaticon-381-calculator-1',
            color: 'info',
            permission: 'CAJA_VER',
            subpages: [
                { title: 'Gestión Caja', to: '/caja/gestion', icon: 'fa fa-cash-register' },
                { title: 'Gastos / Egresos', to: '/caja/gastos', icon: 'fa fa-hand-holding-usd' },
                { title: 'Recaudación Libre', to: '/caja/recaudacion-libre', icon: 'fa fa-file-invoice-dollar' },
                { title: 'Cronogramas', to: '/caja/cronogramas', icon: 'fa fa-calendar-alt' },
                { title: 'Comprobantes', to: '/comprobantes', icon: 'fa fa-receipt' },
                { title: 'Reporte Recaudación', to: '/caja/recaudacion', icon: 'fa fa-chart-pie' },
                { title: 'Config. Cuentas', to: '/caja/cuentas', icon: 'fa fa-university' },
                { title: 'Config. Pagos', to: '/configuracion-pagos', icon: 'fa fa-cog' },
            ]
        },
        {
            title: 'Psicología',
            icon: 'flaticon-381-heart',
            color: 'danger',
            permission: 'PSICO_VER',
            subpages: [
                { title: 'Mis Citas', to: '/psicologia/citas', icon: 'fa fa-calendar-check' },
                { title: 'Horarios Admisión', to: '/psicologia/disponibilidad-admisiones', icon: 'fa fa-user-plus' },
                { title: 'Plantillas', to: '/psicologia/plantillas', icon: 'fa fa-clipboard-list' },
                { title: 'Mi Horario', to: '/psicologia/horario', icon: 'fa fa-clock' },
            ]
        },
        {
            title: 'Seguridad y Config.',
            icon: 'flaticon-381-padlock-1',
            color: 'dark',
            permission: 'SEGURIDAD_VER',
            subpages: [
                { title: 'Roles/Permisos', to: '/seguridad/roles', icon: 'fa fa-user-shield' },
                { title: 'Horarios Lab.', to: '/configuracion/horarios', icon: 'fa fa-user-clock' },
                { title: 'Prueba WhatsApp', to: '/whatsapp-test', icon: 'fa fa-vial' },
            ]
        },
        {
            title: 'Portal Apoderado',
            icon: 'flaticon-381-user-4',
            color: 'secondary',
            role: 'APODERADO',
            subpages: [
                { title: 'Asistencia', to: '/apoderado/asistencia', icon: 'fa fa-calendar-check' },
                { title: 'Notas', to: '/apoderado/notas', icon: 'fa fa-graduation-cap' },
                { title: 'Psicología', to: '/apoderado/psicologia', icon: 'fa fa-heartbeat' },
                { title: 'Pagos', to: '/apoderado/pagos', icon: 'fa fa-money-bill-wave' },
            ]
        },
        {
            title: 'Portal Estudiante',
            icon: 'flaticon-381-user-9',
            color: 'primary',
            role: 'ESTUDIANTE',
            subpages: [
                { title: 'Mis Cursos', to: '/alumno/mis-cursos', icon: 'fa fa-book' },
                { title: 'Mis Calificaciones', to: '/alumno/mis-calificaciones', icon: 'fa fa-star' },
                { title: 'Mis Exámenes', to: '/alumno/mis-examenes', icon: 'fa fa-file-contract' },
                { title: 'Mi Asistencia', to: '/alumno/mi-asistencia', icon: 'fa fa-user-check' },
                { title: 'Mi Horario', to: '/alumno/mi-horario', icon: 'fa fa-calendar-alt' },
            ]
        }
    ];

    // Helper to check visibility (handles both modules and subpages)
    const isVisible = (item) => {
        if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return true;
        if (item.role && !hasRole(item.role)) return false;
        if (item.permission && !hasPermission(item.permission)) return false;
        return true;
    };

    // --- SEARCH LOGIC ---
    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return [];

        const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const results = [];

        modulesConfig.forEach(mod => {
            if (!isVisible(mod)) return;

            mod.subpages.forEach(sub => {
                if (!isVisible(sub)) return;

                const title = sub.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                const modTitle = mod.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

                if (title.includes(query) || modTitle.includes(query)) {
                    results.push({
                        ...sub,
                        moduleTitle: mod.title,
                        moduleColor: mod.color
                    });
                }
            });
        });

        return results;
    }, [searchQuery, modulesConfig]);

    // --- CONFIG: PROCESSES ---
    const processConfig = [
        {
            id: 'matricula',
            title: 'Proceso de Matrícula',
            steps: [
                {
                    title: '1. Registro Postulante',
                    description: 'Crear nuevo usuario estudiante',
                    to: '/usuarios/nuevo-refactored',
                    icon: 'fa fa-user-plus',
                    color: 'primary'
                },
                {
                    title: '2. Citas Psicología',
                    description: 'Agendar y evaluar',
                    to: '/psicologia/agenda',
                    icon: 'fa fa-calendar-check-o',
                    color: 'info'
                },
                {
                    title: '3. Realizar Matrícula',
                    description: 'Asignar grado y sección',
                    to: '/matriculas/nueva',
                    icon: 'fa fa-id-card',
                    color: 'success'
                },
                {
                    title: '4. Gestión de Pagos',
                    description: 'Generar cronograma',
                    to: '/caja/cronogramas',
                    icon: 'fa fa-money',
                    color: 'warning'
                }
            ]
        }
    ];

    // --- DATA: PROCESSES ---
    // kanbanData is now handled by state

    return (
        <div className={`container-fluid ${isDark ? 'dark-mode' : ''}`}>
            {/* --- HEADER & TOGGLE --- */}
            <div className={`row page-titles mx-0 align-items-center ${isDark ? 'bg-dark-mod' : ''}`}>
                <div className="col-sm-6 p-md-0">
                    <div className="welcome-text">
                        <h4>¡Hola, {user?.firstName || 'Usuario'}!</h4>
                        <p className="mb-0">Bienvenido al panel de control de Orion</p>
                    </div>
                </div>
                <div className="col-sm-6 p-md-0 justify-content-sm-end mt-2 mt-sm-0 d-flex gap-3">
                    <div className="search-container flex-grow-1">
                        <div className="input-group search-area d-inline-flex">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="¿Qué estás buscando? (Ej: facturas, alumnos, barcodes...)"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <span className="input-group-text">
                                <i className="flaticon-381-search-2"></i>
                            </span>
                        </div>
                    </div>
                    <div className="btn-group" role="group">
                        <button
                            type="button"
                            className={`btn ${viewMode === 'modules' && !searchQuery ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => {
                                setViewMode('modules');
                                setSearchQuery('');
                            }}
                        >
                            <i className="flaticon-043-menu me-2"></i> Módulos
                        </button>
                        <button
                            type="button"
                            className={`btn ${viewMode === 'processes' && !searchQuery ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => {
                                setViewMode('processes');
                                setSearchQuery('');
                            }}
                        >
                            <i className="flaticon-381-network me-2"></i> Procesos
                        </button>
                    </div>
                </div>
            </div>

            {/* --- CONTENT: SEARCH RESULTS --- */}
            {searchQuery && (
                <div className="row">
                    <div className="col-12">
                        <div className="module-section animate__animated animate__fadeIn">
                            <div className="module-title-container">
                                <div className="module-icon-main bg-gradient-primary">
                                    <i className="flaticon-381-search-2"></i>
                                </div>
                                <h4 className="module-title-text">
                                    Resultados para: <span className="text-primary">"{searchQuery}"</span>
                                </h4>
                            </div>

                            {searchResults.length > 0 ? (
                                <div className="submodule-grid">
                                    {searchResults.map((result, idx) => (
                                        <Link to={result.to} className="submodule-card h-100" key={idx}>
                                            <div className="submodule-icon-box" style={{ color: `var(--bs-${result.moduleColor})` }}>
                                                <i className={result.icon}></i>
                                            </div>
                                            <div className="text-center">
                                                <h5 className="submodule-name">{result.title}</h5>
                                                <small className="text-muted" style={{ fontSize: '0.7rem', display: 'block', marginTop: '4px' }}>
                                                    {result.moduleTitle}
                                                </small>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="fa fa-search-minus fs-1 text-muted mb-3 d-block"></i>
                                    <h5>No encontramos nada que coincida con "{searchQuery}"</h5>
                                    <p className="text-muted">Intenta buscar con otras palabras clave.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- CONTENT: MODULES VIEW --- */}
            {viewMode === 'modules' && !searchQuery && (
                <div className="row">
                    <div className="col-12">
                        {modulesConfig.map((mod, index) => {
                            if (!isVisible(mod)) return null;

                            return (
                                <div className="module-section animate__animated animate__fadeIn" key={index}>
                                    <div className="module-title-container">
                                        <div className={`module-icon-main bg-gradient-${mod.color}`}>
                                            <i className={mod.icon}></i>
                                        </div>
                                        <h4 className="module-title-text">{mod.title}</h4>
                                    </div>

                                    <div className="submodule-grid">
                                        {mod.subpages.map((sub, sIndex) => {
                                            if (!isVisible(sub)) return null;
                                            return (
                                                <Link to={sub.to} className="submodule-card h-100" key={sIndex}>
                                                    <div className="submodule-icon-box" style={{ color: `var(--bs-${mod.color})` }}>
                                                        <i className={sub.icon}></i>
                                                    </div>
                                                    <h5 className="submodule-name text-center">{sub.title}</h5>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* --- CONTENT: PROCESSES VIEW --- */}
            {viewMode === 'processes' && !searchQuery && (
                <div className="row">
                    <div className="col-12">
                        {processConfig.map((proc, pIndex) => (
                            <div className="card" key={pIndex}>
                                <div className="card-header border-0 pb-0">
                                    <h4 className="card-title">{proc.title}</h4>
                                </div>
                                <div className="card-body">
                                    <div className="row justify-content-center">
                                        {/* Simplified Stepper / Flow Visual */}
                                        {proc.steps.map((step, sIndex) => (
                                            <div className="col-xl-3 col-lg-3 col-md-6 mb-3" key={sIndex}>
                                                <div className={`card text-center h-100 border-${step.color}`} style={{ borderTop: `4px solid var(--bs-${step.color})` }}>
                                                    <div className="card-body p-3">
                                                        <div className={`icon-box mb-3 text-${step.color}`}>
                                                            <i className={`${step.icon} fs-2`}></i>
                                                        </div>
                                                        <h5 className="mb-1">{step.title}</h5>
                                                        <small className="d-block mb-3 text-muted">{step.description}</small>
                                                        <Link to={step.to} className={`btn btn-outline-${step.color} btn-xs rounded-pill`}>
                                                            Acceder <i className="fa fa-arrow-right ms-1"></i>
                                                        </Link>
                                                    </div>
                                                    {/* Arrow connector (visual only, hidden on mobile) */}
                                                    {sIndex < proc.steps.length - 1 && (
                                                        <div className="d-none d-lg-block position-absolute start-100 top-50 translate-middle">
                                                            <i className="fa fa-chevron-right text-muted fs-4"></i>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {/* --- KANBAN BOARD SECTION --- */}
                                        <div className="col-12 mt-4">
                                            <hr className="my-5" />
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                <div>
                                                    <h3 className="mb-1">Tablero de Seguimiento</h3>
                                                    <p className="text-muted small">Visualiza el estado de los alumnos en tiempo real</p>
                                                </div>
                                                <div className="kanban-stats-bar d-none d-md-flex">
                                                    <div className="kanban-stat-item">
                                                        <span className="kanban-stat-value text-primary">08</span>
                                                        <span className="kanban-stat-label">Total</span>
                                                    </div>
                                                    <div className="kanban-stat-item">
                                                        <span className="kanban-stat-value text-success">03</span>
                                                        <span className="kanban-stat-label">Aptos</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="kanban-wrapper pb-4">
                                                {loadingKanban ? (
                                                    <div className="col-12 text-center py-5">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="sr-only">Cargando...</span>
                                                        </div>
                                                        <p className="mt-2">Cargando tablero...</p>
                                                    </div>
                                                ) : errorKanban ? (
                                                    <div className="col-12 text-center py-5">
                                                        <div className="alert alert-danger">{errorKanban}</div>
                                                        <button className="btn btn-primary btn-sm" onClick={fetchKanbanData}>Reintentar</button>
                                                    </div>
                                                ) : kanbanData.length === 0 ? (
                                                    <div className="col-12 text-center py-5">
                                                        <p className="text-muted">No hay datos disponibles para mostrar.</p>
                                                    </div>
                                                ) : (
                                                    kanbanData.map((col, cIndex) => (
                                                        <div className="kanban-column" key={cIndex}>
                                                            <div className="kanban-column-header">
                                                                <h5 className="kanban-column-title">
                                                                    <i className={`${col.icon} text-${col.color}`}></i>
                                                                    {col.title}
                                                                </h5>
                                                                <span className="kanban-card-count">{col.cards.length}</span>
                                                            </div>
                                                            <div className="kanban-cards-container">
                                                                {col.cards.map((card, kIndex) => (
                                                                    <div className="kanban-student-card animate__animated animate__fadeInUp" key={kIndex} style={{ animationDelay: `${kIndex * 0.05}s` }}>
                                                                        <div className="student-card-header">
                                                                            <div className="student-avatar">
                                                                                {card.name.split(' ').map(n => n[0]).filter(Boolean).join('').substring(0, 2).toUpperCase()}
                                                                            </div>
                                                                            <h6 className="student-name">{card.name}</h6>
                                                                        </div>
                                                                        <div className="student-card-body">
                                                                            <div className="student-detail">
                                                                                <i className="fa fa-graduation-cap"></i> {card.level} - {card.grade}
                                                                            </div>
                                                                            <div className="student-detail">
                                                                                <i className="fa fa-calendar-day"></i> Registro: {card.date}
                                                                            </div>
                                                                        </div>
                                                                        <div className="student-card-footer">
                                                                            <span className={`student-badge bg-${col.color}-light text-${col.color}`}>
                                                                                {card.status.replace(/_/g, ' ')}
                                                                            </span>
                                                                            <Link to={`/usuarios/estudiantes?id=${card.id}`} className="btn btn-xs btn-link p-0">Ver ficha</Link>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inicio;
